import { makeAutoObservable, runInAction } from 'mobx'
import { notification } from 'antd'
import { openApi } from '../../helpers/request'
import { storeLocal } from '../../helpers/stores'
import i from '../../translations/i'
import { BillingMethod, POLL_INTERVAL_MS, POLL_TIMEOUT_MS, errorMessage } from './billing.helpers'

export interface InvoiceValues {
    company: string
    inn: string
    kpp: string
    address: string
    email: string
    phone: string
    comment: string
}

const emptyInvoice = (email: string = ''): InvoiceValues => ({
    company: '',
    inn: '',
    kpp: '',
    address: '',
    email,
    phone: '',
    comment: '',
})

/** Состояние возврата со страницы оплаты: ждём, пока сервис подтвердит платёж */
export type ResultState = 'checking' | 'paid' | 'failed' | 'waiting'

class BillingController {

    overview: any = null
    loading: boolean = false

    /** Выбранный период оплаты, месяцев */
    months: number = 1

    payments: any[] = []
    paymentsLoading: boolean = false
    pagination = {
        total: 0,
        page: 1,
        limit: 10,
    }

    checkout = {
        open: false,
        plan: null as any,
        quote: null as any,
        quoteLoading: false,
        quoteError: null as string | null,
        method: null as BillingMethod | null,
        email: '',
        submitting: false,
    }

    invoice = {
        open: false,
        plan: null as any,
        values: emptyInvoice(),
        submitting: false,
    }

    result = {
        uuid: null as string | null,
        state: null as ResultState | null,
        payment: null as any,
    }

    private pollTimer: ReturnType<typeof setTimeout> | null = null
    private pollStartedAt: number = 0
    private quoteRequestId: number = 0

    constructor() {
        makeAutoObservable<BillingController, 'pollTimer' | 'pollStartedAt' | 'quoteRequestId'>(this, {
            pollTimer: false,
            pollStartedAt: false,
            quoteRequestId: false,
        })
    }

    /** Периоды, на которые можно купить хоть один тариф: 1, 12, ... */
    get periods(): number[] {
        const set = new Set<number>()

        this.plans.forEach((plan: any) => plan.prices.forEach((price: any) => set.add(price.months)))

        return Array.from(set).sort((a, b) => a - b)
    }

    get plans(): any[] {
        return this.overview?.plans ?? []
    }

    get methods(): BillingMethod[] {
        return this.overview?.methods ?? []
    }

    /** Можно ли заплатить прямо сейчас — картой/СБП или заявкой на счёт */
    get canPay(): boolean {
        return !!this.overview?.availability?.available && (this.methods.length > 0 || !!this.overview?.invoice)
    }

    /** Наибольшая выгода среди тарифов на этот период — для подписи переключателя */
    maxDiscount(months: number): number {
        return this.plans.reduce((max: number, plan: any) => {
            const price = plan.prices.find((p: any) => p.months === months)

            return price ? Math.max(max, price.discount_percent) : max
        }, 0)
    }

    price(plan: any, months: number = this.months): any | null {
        return plan?.prices?.find((price: any) => price.months === months) ?? null
    }

    load(silent: boolean = false) {
        if (!silent) this.loading = true

        return openApi('get', '/billing', {}, undefined, silent)
            .then(result => runInAction(() => {
                const firstLoad = this.overview === null

                this.overview = result.data

                if (firstLoad) {
                    // По умолчанию — самый выгодный период, обычно год
                    const periods = this.periods
                    this.months = periods.length > 0 ? periods[periods.length - 1] : 1
                }

                this.syncPricingPlan()
            }))
            .catch(err => notification.error({ message: i.t('billing:errors.load'), description: errorMessage(err) }))
            .finally(() => runInAction(() => this.loading = false))
    }

    loadPayments(page: number = this.pagination.page) {
        this.paymentsLoading = true

        openApi('get', `/billing/payments?page=${page}&limit=${this.pagination.limit}`, {}, undefined, true)
            .then(result => runInAction(() => {
                this.payments = result.data
                this.pagination.total = result.total
                this.pagination.page = Number(result.page)
            }))
            .catch(err => console.log(err))
            .finally(() => runInAction(() => this.paymentsLoading = false))
    }

    setMonths(months: number) {
        this.months = months

        if (this.checkout.open && this.checkout.plan) {
            if (this.price(this.checkout.plan, months)) {
                this.loadQuote()
            } else {
                this.closeCheckout()
            }
        }
    }

    openCheckout(plan: any) {
        this.checkout.open = true
        this.checkout.plan = plan
        this.checkout.method = this.checkout.method && this.methods.includes(this.checkout.method)
            ? this.checkout.method
            : (this.methods[0] ?? null)
        this.checkout.email = this.checkout.email || this.overview?.default_email || ''

        this.loadQuote()
    }

    /** Продление текущего тарифа: на выбранный период, а если на него тариф не продаётся — на первый доступный */
    openRenew() {
        if (!this.canPay) return

        const plan = this.plans.find((item: any) => item.level === this.overview?.subscription?.level)
        if (!plan || plan.prices.length === 0) return

        if (!this.price(plan)) this.months = plan.prices[plan.prices.length - 1].months

        this.openCheckout(plan)
    }

    closeCheckout() {
        this.checkout.open = false
        this.checkout.quote = null
        this.checkout.quoteError = null
    }

    setMethod(method: BillingMethod) {
        this.checkout.method = method
    }

    setEmail(email: string) {
        this.checkout.email = email
    }

    private loadQuote() {
        const plan = this.checkout.plan
        if (!plan) return

        const requestId = ++this.quoteRequestId
        this.checkout.quoteLoading = true
        this.checkout.quoteError = null

        openApi('get', `/billing/quote?plan=${plan.level}&months=${this.months}`, {}, undefined, true)
            .then(result => runInAction(() => {
                // Пока летел ответ, период могли переключить
                if (requestId === this.quoteRequestId) this.checkout.quote = result.data
            }))
            .catch(err => runInAction(() => {
                if (requestId === this.quoteRequestId) this.checkout.quoteError = errorMessage(err)
            }))
            .finally(() => runInAction(() => {
                if (requestId === this.quoteRequestId) this.checkout.quoteLoading = false
            }))
    }

    /** Создаём ссылку и уводим на страницу оплаты */
    pay() {
        const { plan, method, email } = this.checkout
        if (!plan || !method || this.checkout.submitting) return

        this.checkout.submitting = true

        openApi('post', '/billing/payments', { plan: plan.level, months: this.months, method, email })
            .then(result => {
                const url = result.data?.payment_url

                if (!url) throw new Error(i.t('billing:errors.noLink'))

                window.location.href = url
            })
            .catch(err => runInAction(() => {
                this.checkout.submitting = false
                notification.error({ message: i.t('billing:errors.pay'), description: err?.response ? errorMessage(err) : err?.message })
            }))
    }

    openInvoice(plan: any = this.checkout.plan) {
        this.closeCheckout()
        this.invoice.open = true
        this.invoice.plan = plan
        this.invoice.values = { ...emptyInvoice(this.overview?.default_email ?? ''), ...this.invoice.values, email: this.invoice.values.email || this.overview?.default_email || '' }
    }

    closeInvoice() {
        this.invoice.open = false
    }

    setInvoicePlan(level: number) {
        this.invoice.plan = this.plans.find((plan: any) => plan.level === level) ?? null
    }

    setInvoiceValue(key: keyof InvoiceValues, value: string) {
        this.invoice.values[key] = value
    }

    submitInvoice() {
        const plan = this.invoice.plan
        if (!plan || this.invoice.submitting) return

        this.invoice.submitting = true

        openApi('post', '/billing/invoice', { plan: plan.level, months: this.months, ...this.invoice.values })
            .then(() => runInAction(() => {
                this.invoice.open = false
                this.invoice.values = emptyInvoice(this.overview?.default_email ?? '')
                notification.success({ message: i.t('billing:invoice.sent'), description: i.t('billing:invoice.sentHint') })
                this.load(true)
                this.loadPayments(1)
            }))
            .catch(err => notification.error({ message: i.t('billing:errors.invoice'), description: errorMessage(err) }))
            .finally(() => runInAction(() => this.invoice.submitting = false))
    }

    cancelInvoice(uuid: string) {
        openApi('post', `/billing/payments/${uuid}/cancel`)
            .then(() => {
                notification.success({ message: i.t('billing:invoice.canceled') })
                this.load(true)
                this.loadPayments()
            })
            .catch(err => notification.error({ message: i.t('billing:errors.common'), description: errorMessage(err) }))
    }

    /** Только сотрудники платформы: счёт оплачен по реквизитам */
    confirmInvoice(uuid: string) {
        openApi('post', `/billing/payments/${uuid}/confirm`)
            .then(() => {
                notification.success({ message: i.t('billing:invoice.confirmed') })
                this.load(true)
                this.loadPayments()
            })
            .catch(err => notification.error({ message: i.t('billing:errors.common'), description: errorMessage(err) }))
    }

    /** Клиент вернулся со страницы оплаты: опрашиваем статус, пока сервис не подтвердит */
    startResult(uuid: string, failed: boolean) {
        this.stopPolling()

        this.result.uuid = uuid
        this.result.payment = null
        this.result.state = 'checking'
        this.pollStartedAt = Date.now()

        // Даже при возврате по «неуспешному» адресу один раз проверяем: клиент мог оплатить со второй попытки
        this.poll(failed)
    }

    private poll(failedReturn: boolean) {
        const uuid = this.result.uuid
        if (!uuid) return

        openApi('get', `/billing/payments/${uuid}`, {}, undefined, true)
            .then(result => runInAction(() => {
                if (this.result.uuid !== uuid) return

                const payment = result.data
                this.result.payment = payment

                if (payment.status === 'paid') {
                    this.result.state = 'paid'
                    this.load(true)
                    this.loadPayments(1)

                    return
                }

                if (payment.status !== 'pending') {
                    this.result.state = 'failed'

                    return
                }

                // Вернулся по «неуспешному» адресу, и сервис оплату не подтвердил — оплаты не было
                if (failedReturn) {
                    this.result.state = 'failed'

                    return
                }

                if (Date.now() - this.pollStartedAt > POLL_TIMEOUT_MS) {
                    this.result.state = 'waiting'

                    return
                }

                this.pollTimer = setTimeout(() => this.poll(failedReturn), POLL_INTERVAL_MS)
            }))
            .catch(() => runInAction(() => {
                if (this.result.uuid !== uuid) return

                if (Date.now() - this.pollStartedAt > POLL_TIMEOUT_MS) {
                    this.result.state = 'waiting'
                } else {
                    this.pollTimer = setTimeout(() => this.poll(failedReturn), POLL_INTERVAL_MS)
                }
            }))
    }

    stopPolling() {
        if (this.pollTimer) clearTimeout(this.pollTimer)
        this.pollTimer = null
    }

    closeResult() {
        this.stopPolling()
        this.result.uuid = null
        this.result.state = null
        this.result.payment = null
    }

    /**
     * Ограничения тарифа в админке читаются из localStorage (pricing_plan) — после оплаты
     * обновляем его сразу, без перелогина.
     */
    private syncPricingPlan() {
        const level = this.overview?.subscription?.level

        if (typeof level === 'number' && storeLocal.get('pricing_plan') !== level) {
            storeLocal.set('pricing_plan', level)
        }
    }
}

export default new BillingController()
